import React, { useRef, useState } from 'react';
import { auth } from '../services/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { X, Mail, Loader2, ArrowRight } from 'lucide-react';
import { useModalAccessibility } from '../hooks/useModalAccessibility';

interface ResetPasswordModalProps {
  onClose: () => void;
  defaultEmail?: string;
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ onClose, defaultEmail = '' }) => {
  const [email, setEmail] = useState(defaultEmail);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);

  useModalAccessibility(dialogRef, { onClose });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setStatus('idle');

    try {
      await sendPasswordResetEmail(auth, email);
      setStatus('success');
    } catch (error: any) {
      setStatus('error');
      if (error.code === 'auth/user-not-found') {
        setErrorMsg('No existe ninguna cuenta con este correo.');
      } else if (error.code === 'auth/invalid-email') {
        setErrorMsg('El correo electrónico no es válido.');
      } else {
        setErrorMsg('Ocurrió un error. Inténtalo de nuevo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Recuperar contrasena"
        tabIndex={-1}
        className="bg-panel rounded-2xl shadow-2xl w-full max-w-sm max-h-[90dvh] overflow-y-auto relative"
      >

        {/* Botón cerrar */}
        <button onClick={onClose} className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center hover:bg-raised rounded-full text-muted transition-colors" aria-label="Cerrar recuperacion de contrasena">
          <X size={18} />
        </button>

        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-raised rounded-full flex items-center justify-center mx-auto mb-3 text-juris">
              <Mail size={24} />
            </div>
            <h3 className="text-lg font-bold text-ink">Recuperar contraseña</h3>
            <p className="text-xs text-muted mt-1">
              Introduce tu email y te enviaremos las instrucciones.
            </p>
          </div>

          {status === 'success' ? (
            <div className="text-center space-y-4 motion-fade-through">
              <div className="bg-success-soft text-success text-sm p-4 rounded-xl border border-success leading-relaxed">
                <strong>¡Correo enviado!</strong><br />
                Revisa tu bandeja de entrada. Si no lo encuentras, por favor <strong>revisa la carpeta de Spam o Correo no deseado</strong>.
              </div>
              <button
                onClick={onClose}
                className="w-full min-h-11 py-3 bg-action text-white rounded-xl font-bold text-sm hover:bg-action-hover transition-colors"
              >
                Volver al inicio
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <input
                  type="email"
                  placeholder="ejemplo@correo.com"
                  aria-label="Correo electronico para recuperar contrasena"
                  aria-invalid={status === 'error'}
                  aria-describedby={status === 'error' ? 'reset-password-error' : undefined}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-canvas border border-line text-ink text-sm rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-juris focus:border-transparent transition-all"
                />
                {status === 'error' && (
                  <p id="reset-password-error" role="alert" className="text-xs text-danger pl-1">{errorMsg}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full min-h-11 py-3 rounded-xl font-bold text-white shadow-md flex items-center justify-center gap-2 transition-all text-sm
                  ${loading ? 'bg-action/70 cursor-not-allowed' : 'bg-action hover:bg-action-hover hover:shadow-lg transform active:scale-[0.98]'}
                `}
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <>Enviar enlace <ArrowRight size={16} /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
